import { useSyncExternalStore } from 'react';
import utils from './utils';
import { API } from '../service/ProductsService';

// רשימת הקניות - store משותף (כמו ה-stores באפליקציה, מדריך הארכיטקטורה סעיף 8), נשמרת בשרת (ShoppingList.sql):
// עוברת בין מכשירים, ואפשר לשתף אותה עם בני הבית (כמה משתמשים על אותה רשימה).
// רק למשתמשים רשומים. אחרי כניסה / יציאה ZolBakfarApp קורא ל-basket.reload().
//
// שינויים אופטימיים: הרשימה בקליינט משתנה מיד, והבקשה נשלחת לשרת. כל תשובה מחזירה את הרשימה המלאה -
// היא מחליפה את מה שיש, רק אם לא נשלח שינוי חדש יותר בינתיים (seq). שינוי כמות נשלח אחרי הפסקה קצרה (QTY_DEBOUNCE_MS)
// כמצב רצוי (mode 'set'), כך שלחיצות מהירות על +/− הן בקשה אחת.
// רשימה משותפת מתרעננת כל REFRESH_MS כשהחלון גלוי, וגם כשחוזרים לחלון.
// פריט: { id (platformProductId - item.id בחיפוש), name, isWeighable, imageUrl, qty, addedByName }. במוצר שקיל qty = ק"ג.

const MAX_ITEMS = 100;          // כמו @maxItems ב-spSaveShoppingListItems / spCompareBasket
const QTY_DEBOUNCE_MS = 400;
const REFRESH_MS = 20000;
const LOCAL_PREFIX = 'shopping-list';   // לפני שהרשימה עברה לשרת - מועלית פעם אחת ונמחקת

let items = [];
// list: { id, shareCode, memberCount, isOwner }, members: [{ userId, firstName, lastName, isOwner, isMe }]
let meta = { loaded: false, list: null, members: [], error: null };
const listeners = new Set();
const notify = () => listeners.forEach((l) => l());

let seq = 0;          // מספר השינוי האחרון שנשלח
let inFlight = 0;     // שינויים שעוד לא חזרו
let loadId = 0;       // טעינה של משתמש קודם לא נכנסת אחרי יציאה / כניסה
const qtyTimers = new Map();
const pendingQty = new Map();   // id → כמות שעוד לא נשלחה (בזמן ה-debounce). גוברת על מה שחוזר מהשרת

export const minQty = (item) => (item.isWeighable ? 0.5 : 1);
export const qtyStep = (item) => (item.isWeighable ? 0.5 : 1);
const roundQty = (item, qty) => Math.max(minQty(item), item.isWeighable ? Math.round(qty * 2) / 2 : Math.round(qty));

const setItems = (next) => { items = next; notify(); };
const setMeta = (patch) => { meta = { ...meta, ...patch }; notify(); };

const fromServer = (resp) => ({
  items: (resp[3] || []).map((r) => ({
    id: r.id, name: r.name, isWeighable: !!r.isWeighable, imageUrl: r.imageUrl, qty: Number(r.qty), addedByName: r.addedByName,
  })),
  list: resp[1]?.[0] || null,
  members: resp[2] || [],
});

const applyList = (resp) => {
  const data = fromServer(resp);
  items = pendingQty.size
    ? data.items.map((x) => (pendingQty.has(x.id) ? { ...x, qty: pendingQty.get(x.id) } : x))
    : data.items;
  meta = { ...meta, loaded: true, list: data.list, members: data.members, error: null };
  notify();
  scheduleRefresh();
};

// בקשה שמשנה את הרשימה. התשובה מוחלת רק אם זה השינוי האחרון; שגיאה → הודעה + טעינה מחדש מהשרת
const mutate = async (request) => {
  const mySeq = ++seq;
  const myLoad = loadId;
  inFlight++;
  try {
    const resp = await request();
    if (myLoad !== loadId) return null;
    const status = resp?.[0]?.[0];
    if (status?.code === 0) {
      if (mySeq === seq) applyList(resp);
      return resp;
    }
    if (status?.code !== 90) setMeta({ error: status?.msg || 'שגיאה בשמירת הרשימה' });
    refresh();
    return null;
  } catch (err) {
    if (myLoad === loadId) { setMeta({ error: 'אין חיבור לשרת - השינוי לא נשמר' }); refresh(); }
    return null;
  } finally {
    inFlight--;
  }
};

// טעינה מהשרת (כניסה, רענון של רשימה משותפת). לא דורסת שינויים שבדרך
const refresh = async () => {
  if (!utils.getCurrentUser()) return;
  const mySeq = seq;
  const myLoad = loadId;
  try {
    const resp = await API.getShoppingList();
    if (myLoad !== loadId || mySeq !== seq || inFlight > 0) return;
    if (resp?.[0]?.[0]?.code === 0) applyList(resp);
  } catch (e) { /* ננסה ברענון הבא */ }
};

// רשימה שנשמרה בדפדפן לפני שעברנו לשרת - מתווספת לרשימה בשרת פעם אחת
const migrateLocal = async () => {
  const user = utils.getCurrentUser();
  const keys = [LOCAL_PREFIX, `${LOCAL_PREFIX}-${user?.userId}`, `${LOCAL_PREFIX}-session`];
  let local = [];
  try {
    keys.forEach((k) => {
      const saved = JSON.parse(localStorage.getItem(k));
      if (Array.isArray(saved)) local = local.concat(saved.filter((x) => x && x.id));
    });
  } catch (e) { return; }
  if (!local.length) return;
  const resp = await mutate(() => API.saveShoppingListItems('add', local.slice(0, MAX_ITEMS).map((x) => ({ id: x.id, qty: x.qty }))));
  if (resp) { try { keys.forEach((k) => localStorage.removeItem(k)); } catch (e) { /* חסום */ } }
};

// רענון אוטומטי - רק לרשימה משותפת, כשהחלון גלוי
let refreshTimer = null;
function scheduleRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = null;
  if (!utils.getCurrentUser() || (meta.list?.memberCount || 0) < 2) return;
  refreshTimer = setTimeout(() => {
    if (document.visibilityState === 'visible') refresh(); else scheduleRefresh();
  }, REFRESH_MS);
}

if (typeof window !== 'undefined') {
  const onBack = () => { if (document.visibilityState === 'visible' && meta.loaded) refresh(); };
  window.addEventListener('focus', onBack);
  document.addEventListener('visibilitychange', onBack);
}

const sendQty = (id, qty) => {
  pendingQty.set(id, qty);
  clearTimeout(qtyTimers.get(id));
  qtyTimers.set(id, setTimeout(() => {
    qtyTimers.delete(id);
    const value = pendingQty.get(id);
    pendingQty.delete(id);
    mutate(() => API.saveShoppingListItems('set', [{ id, qty: value }]));
  }, QTY_DEBOUNCE_MS));
};

const cancelQty = (id) => {
  if (id === undefined) {
    qtyTimers.forEach((t) => clearTimeout(t));
    qtyTimers.clear();
    pendingQty.clear();
    return;
  }
  clearTimeout(qtyTimers.get(id));
  qtyTimers.delete(id);
  pendingQty.delete(id);
};

export const basket = {
  subscribe: (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
  getItems: () => items,
  getMeta: () => meta,
  has: (id) => items.some((x) => x.id === id),

  // אחרי כניסה / יציאה - הרשימה של המשתמש הנוכחי (ריקה כשאין משתמש)
  reload: async () => {
    const myLoad = ++loadId;
    cancelQty();
    items = [];
    meta = { loaded: false, list: null, members: [], error: null };
    notify();
    scheduleRefresh();
    if (!utils.getCurrentUser()) return;
    await refresh();
    if (myLoad === loadId) await migrateLocal();
  },
  refresh,
  clearError: () => setMeta({ error: null }),

  // item: אובייקט מוצר מהחיפוש / הרשימות (id, name, isWeighable, imageUrl). מוצר שכבר ברשימה - הכמות גדלה
  add: (item) => {
    const existing = items.find((x) => x.id === item.id);
    if (!existing && items.length >= MAX_ITEMS) return false;
    const step = existing ? qtyStep(existing) : minQty(item);
    setItems(existing
      ? items.map((x) => (x.id === item.id ? { ...x, qty: x.qty + step } : x))
      : [...items, { id: item.id, name: item.name, isWeighable: !!item.isWeighable, imageUrl: item.imageUrl || null, qty: step }]);
    mutate(() => API.saveShoppingListItems('add', [{ id: item.id, qty: step }]));
    return true;
  },
  // כמה מוצרים בבת אחת (טעינת רשימה, מועדפים): [{ item, qty }]. מוצר שכבר ברשימה - הכמות מתווספת. מחזיר כמה נוספו / עודכנו
  addMany: (entries) => {
    let next = [...items];
    const sent = [];
    entries.forEach(({ item, qty }) => {
      const q = roundQty(item, Number(qty || 1));
      if (next.some((x) => x.id === item.id)) {
        next = next.map((x) => (x.id === item.id ? { ...x, qty: x.qty + q } : x));
      } else if (next.length < MAX_ITEMS) {
        next.push({ id: item.id, name: item.name, isWeighable: !!item.isWeighable, imageUrl: item.imageUrl || null, qty: q });
      } else return;
      sent.push({ id: item.id, qty: q });
    });
    if (!sent.length) return 0;
    setItems(next);
    mutate(() => API.saveShoppingListItems('add', sent));
    return sent.length;
  },
  setQty: (id, qty) => {
    const x = items.find((i) => i.id === id);
    if (!x) return;
    const value = roundQty(x, qty);
    setItems(items.map((i) => (i.id === id ? { ...i, qty: value } : i)));
    sendQty(id, value);
  },
  remove: (id) => {
    cancelQty(id);
    setItems(items.filter((x) => x.id !== id));
    mutate(() => API.saveShoppingListItems('set', [{ id, qty: 0 }]));
  },
  // מרוקן ושומר ב"רשימות קודמות" (spClearShoppingList)
  clear: () => {
    cancelQty();
    setItems([]);
    return mutate(() => API.clearShoppingList());
  },

  // הוספה מהירה - השרת מוסיף (המועדפים שעוד לא ברשימה / כל רשימה קודמת). מחזיר כמה מוצרים חדשים נכנסו, או null בשגיאה
  addFavorites: async () => {
    const resp = await mutate(() => API.addFavoritesToShoppingList());
    return resp ? (resp[4]?.[0]?.addedCount ?? 0) : null;
  },
  addArchive: async (archiveId) => {
    const resp = await mutate(() => API.addArchiveToShoppingList(archiveId));
    return resp ? (resp[4]?.[0]?.addedCount ?? 0) : null;
  },

  // שיתוף עם בני הבית. מחזירים { ok, msg, ... }
  share: async (regenerate = false) => {
    try {
      const resp = await API.shareShoppingList(regenerate);
      const status = resp?.[0]?.[0];
      if (status?.code !== 0) return { ok: false, msg: status?.msg || 'שגיאה ביצירת הקישור' };
      const shareCode = resp[1]?.[0]?.shareCode;
      if (meta.list) setMeta({ list: { ...meta.list, shareCode } });
      return { ok: true, shareCode };
    } catch (err) {
      return { ok: false, msg: 'אין חיבור לשרת' };
    }
  },
  join: async (code) => {
    const resp = await mutate(() => API.joinShoppingList(code));
    return resp ? { ok: true } : { ok: false, msg: meta.error };
  },
  leave: async () => {
    setItems([]);
    const resp = await mutate(() => API.leaveShoppingList());
    return resp ? { ok: true } : { ok: false, msg: meta.error };
  },
};

if (utils.getCurrentUser()) basket.reload();

// הרשימה העדכנית; הקומפוננטה מתרנדרת בכל שינוי
export const useBasket = () => useSyncExternalStore(basket.subscribe, basket.getItems);
// פרטי הרשימה: { loaded, list, members, error }
export const useBasketMeta = () => useSyncExternalStore(basket.subscribe, basket.getMeta);
